import { useQuery } from '@tanstack/react-query';
import { can, localDateOf, localWallTimeUtc, type Role } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useOrgClock } from '../../lib/useOrgClock';
import { minutesInTz } from '../../lib/format';
import { Field, field } from '../create/Field';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';

export type QuotePlanningDraft = {
  estimatorUserId?: string;
  requestedStartAt?: string;
  requestedEndAt?: string;
};

export type SavedQuotePlanning = {
  estimatorUserId: string | null;
  requestedStartAt: string | Date | null;
  requestedEndAt: string | Date | null;
};

export function QuotePlanningSummary({ value }: { value: SavedQuotePlanning }) {
  const trpc = useTRPC();
  const members = useQuery(trpc.org.members.queryOptions());
  const clock = useOrgClock();
  return (
    <div className="space-y-2 text-sm">
      <p>
        <span className="text-muted-foreground">Estimator: </span>
        {value.estimatorUserId
          ? (members.data?.find((member) => member.userId === value.estimatorUserId)
              ?.name ?? 'Selected estimator unavailable')
          : 'Unassigned'}
      </p>
      <p>
        <span className="text-muted-foreground">Requested window: </span>
        {value.requestedStartAt && value.requestedEndAt
          ? clock.tz
            ? `${quoteWindowInput(value.requestedStartAt, clock.tz).replace('T', ' ')} – ${quoteWindowInput(value.requestedEndAt, clock.tz).replace('T', ' ')} · ${clock.tz}`
            : clock.state === 'loading'
              ? 'Loading shop timezone…'
              : 'Shop timezone unavailable'
          : 'Not proposed'}
      </p>
      <p className="text-xs text-muted-foreground">
        Requested dates and crew assignments are advisory. A visit must be booked
        separately.
      </p>
    </div>
  );
}

/** Format an existing instant for a datetime-local control using the shop's clock. */
export function quoteWindowInput(
  value: string | Date | undefined | null,
  timezone: string,
): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const minutes = minutesInTz(date, timezone);
  return `${localDateOf(date, timezone)}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Captures a user's shop-local input; the server validates the interval and availability. */
export function quoteWindowInstant(value: string, timezone: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return undefined;
  const [date, time] = value.split('T');
  const [hour, minute] = time!.split(':').map(Number);
  return localWallTimeUtc(date!, hour! * 60 + minute!, timezone).toISOString();
}

export function QuotePlanningFields({
  value,
  onChange,
  disabled = false,
}: {
  value: QuotePlanningDraft;
  onChange: (value: QuotePlanningDraft) => void;
  disabled?: boolean;
}) {
  const trpc = useTRPC();
  const members = useQuery(trpc.org.members.queryOptions());
  const clock = useOrgClock();
  const estimators = (members.data ?? []).filter((member) =>
    can(member.role as Role, 'quote', 'update'),
  );
  return (
    <fieldset disabled={disabled} className="space-y-3">
      <legend className="text-sm font-semibold">
        Quote responsibility and requested window
      </legend>
      {members.isError || clock.state !== 'ready' ? (
        <Notice tone="warn">
          Some planning choices are unavailable. Saved values remain intact; reconnect or
          refresh before changing them.
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void members.refetch();
              clock.refetch();
            }}
          >
            Refresh planning choices
          </Button>
        </Notice>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Estimator">
          <select
            className={field}
            value={value.estimatorUserId ?? ''}
            disabled={disabled || members.isPending || members.isError}
            onChange={(event) =>
              onChange({ ...value, estimatorUserId: event.target.value || undefined })
            }
          >
            <option value="">Unassigned</option>
            {value.estimatorUserId &&
            !estimators.some((member) => member.userId === value.estimatorUserId) ? (
              <option value={value.estimatorUserId}>Previously assigned estimator</option>
            ) : null}
            {estimators.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name ?? member.email}
              </option>
            ))}
          </select>
        </Field>
        <Field label={`Requested start${clock.tz ? ` · ${clock.tz}` : ''}`}>
          <input
            className={field}
            type="datetime-local"
            disabled={disabled || !clock.tz}
            value={clock.tz ? quoteWindowInput(value.requestedStartAt, clock.tz) : ''}
            onChange={(event) => {
              if (clock.tz)
                onChange({
                  ...value,
                  requestedStartAt: quoteWindowInstant(event.target.value, clock.tz),
                });
            }}
          />
        </Field>
        <Field label="Requested end">
          <input
            className={field}
            type="datetime-local"
            disabled={disabled || !clock.tz}
            value={clock.tz ? quoteWindowInput(value.requestedEndAt, clock.tz) : ''}
            onChange={(event) => {
              if (clock.tz)
                onChange({
                  ...value,
                  requestedEndAt: quoteWindowInstant(event.target.value, clock.tz),
                });
            }}
          />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">
        The estimator prepares the quote. Requested dates and line crew assignments are
        proposals; they do not book a visit or hold a crew.
      </p>
    </fieldset>
  );
}
